import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  UserPlus, Loader2, Search, Play, Pause, Plus, Building2, Send, CheckCircle2,
  Clock, XCircle, ShieldCheck, ExternalLink, UserCheck, RefreshCw, MessageSquare,
  Pencil, Trash2, X, ArrowRightLeft,
} from "lucide-react";

const STAT_META = [
  { key: "candidates", label: "Found", icon: Building2, cls: "text-foreground" },
  { key: "pending", label: "Queued", icon: Clock, cls: "text-amber-accent" },
  { key: "sent", label: "Invites sent", icon: Send, cls: "text-signal-500" },
  { key: "accepted", label: "Accepted", icon: CheckCircle2, cls: "text-emerald-400" },
  { key: "followed_up", label: "Followed-up", icon: UserCheck, cls: "text-violet-400" },
  { key: "failed", label: "Failed", icon: XCircle, cls: "text-red-400" },
];

const FALLBACK_NOTES = [
  "Hi {first_name}, I work with Europacific in Koper logistics. Always great to connect with professionals across shipping, ports & supply chains. Feel free to follow Europacific for freight market insights and logistics updates.",
  "Hi {first_name}, I enjoy connecting with people across the logistics and forwarding community. Looking forward to exchanging insights. You're also welcome to follow Europacific for industry news and market updates.",
  "Hi {first_name}, I'm part of the Europacific team in Koper. I regularly share logistics insights, freight trends and port updates. Happy to connect—and feel free to follow Europacific for more.",
  "Hi {first_name}, always happy to connect with professionals in logistics, shipping and supply chains. Looking forward to staying in touch. Feel free to follow Europacific for useful industry updates.",
  "Hi {first_name}, I work with Europacific in Koper. Always happy to connect with professionals across logistics, shipping and supply chains. Looking forward to staying in touch!",
];
const FALLBACK_FOLLOWUP = "Thanks for connecting! We regularly share freight market updates, port news and practical logistics insights through Europacific. If that's of interest, we'd be delighted if you followed our page. Always happy to exchange ideas on logistics as well.";

const POSTS_PER_PAGE_TO_SCAN = 10;

export default function LinkedInConnectPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("Competitor outreach");
  const [companies, setCompanies] = useState("Port of Koper\nIntereuropa\nActual I.T.");
  const [dailyLimit, setDailyLimit] = useState(20);
  const [notes, setNotes] = useState(FALLBACK_NOTES);
  const [followup, setFollowup] = useState(FALLBACK_FOLLOWUP);
  const [followupEnabled, setFollowupEnabled] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/linkedin/connect/campaigns"); setCampaigns(data.campaigns || []); }
    catch { toast.error("Failed to load campaigns"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/linkedin/connect/defaults")
      .then(({ data }) => {
        if (data.notes?.length) setNotes(data.notes);
        if (data.followup_message) setFollowup(data.followup_message);
      })
      .catch(() => { /* keep fallbacks */ });
  }, []);

  const setNoteAt = (i, val) => setNotes((prev) => prev.map((n, idx) => (idx === i ? val : n)));

  const create = async () => {
    const list = companies.split("\n").map((c) => c.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one competitor company"); return; }
    const cleanNotes = notes.map((n) => n.trim()).filter(Boolean);
    if (!cleanNotes.length) { toast.error("Add at least one connection note"); return; }
    setCreating(true);
    try {
      await api.post("/linkedin/connect/campaigns", {
        name, companies: list, daily_limit: Number(dailyLimit),
        notes: cleanNotes, followup_message: followup.trim(),
        followup_enabled: followupEnabled && !!followup.trim(),
      });
      toast.success("Campaign created — run Discover to find people");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create campaign"); }
    setCreating(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="li-connect-page">
        <div>
          <div className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase">Growth</div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><UserPlus className="w-7 h-7 text-signal-500" /> LinkedIn Connect Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            A two-step play: find the people who <b>like &amp; comment on a company page's posts</b>
            {" "}(its active audience) and auto-send them connection requests at a safe daily pace —
            rotating up to 5 note variations. When someone accepts, we wait 1–2 days, then send a
            warm follow-up DM inviting them to follow your Europacific page.
          </p>
        </div>

        <div className="bg-amber-accent/10 border border-amber-accent/30 rounded-xl p-4 flex gap-3 text-sm" data-testid="li-connect-safety">
          <ShieldCheck className="w-5 h-5 text-amber-accent shrink-0 mt-0.5" />
          <div className="text-muted-foreground">
            <b className="text-foreground">Kept safe automatically.</b> LinkedIn limits connection
            invites (~20–25/day). Volume is capped per day with human-like spacing, and the drip
            stops if LinkedIn rate-limits — protecting the account. LinkedIn doesn't expose a
            page's <b>follower</b> list, so we target the people who <b>react to &amp; comment on
            the page's posts</b> — its active, engaged audience — instead.
          </div>
        </div>

        {/* Create */}
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="li-connect-create">
          <div className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-signal-500" /> New campaign</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Campaign name</label>
              <Input data-testid="li-connect-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Connection requests per day</label>
              <div className="flex gap-2 mt-1">
                {[15, 20, 25].map((n) => (
                  <button key={n} data-testid={`li-connect-limit-${n}`} onClick={() => setDailyLimit(n)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${Number(dailyLimit) === n ? "bg-signal-500 text-white border-signal-500" : "border-border text-muted-foreground"}`}>{n}/day</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Target company pages (one per line) — name, LinkedIn handle, or full page URL</label>
            <Textarea data-testid="li-connect-companies" value={companies} onChange={(e) => setCompanies(e.target.value)} rows={4} className="mt-1 text-sm" placeholder={"Port of Koper\nhttps://www.linkedin.com/company/luka-koper\nIntereuropa"} />
            <p className="text-[11px] text-muted-foreground mt-1">We scan each page's last {POSTS_PER_PAGE_TO_SCAN} posts and queue everyone who reacted or commented. Tip: pasting the exact LinkedIn page URL resolves most reliably.</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Connection notes — up to 5, rotated across people. Use {"{first_name}"} (max 300 chars each)</label>
            <div className="space-y-2 mt-1.5" data-testid="li-connect-notes">
              {notes.map((n, i) => (
                <div key={i} className="relative">
                  <span className="absolute left-2 top-2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-signal-500/15 text-signal-500 z-10">#{i + 1}</span>
                  <Textarea
                    data-testid={`li-connect-note-${i}`}
                    value={n}
                    onChange={(e) => setNoteAt(i, e.target.value)}
                    rows={2}
                    maxLength={300}
                    className="text-sm pl-10"
                    placeholder={`Connection note variation ${i + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-violet-400/30 bg-violet-400/5 p-4 space-y-2" data-testid="li-connect-followup">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-semibold">After they accept — follow-up DM</span>
              <button
                type="button"
                onClick={() => setFollowupEnabled((v) => !v)}
                data-testid="li-connect-followup-toggle"
                className={`ml-auto relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${followupEnabled ? "bg-violet-500" : "bg-muted"}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${followupEnabled ? "translate-x-5" : "translate-x-1"}`} />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">Sent automatically 1–2 days after they connect, inviting them to follow your page. We automatically append a direct <strong>one-click link to your LinkedIn page</strong> at the end of this DM — so following takes the fewest clicks possible (you don't need to paste the link yourself).</p>
            <Textarea
              data-testid="li-connect-followup-msg"
              value={followup}
              onChange={(e) => setFollowup(e.target.value)}
              rows={3}
              maxLength={900}
              disabled={!followupEnabled}
              className="text-sm disabled:opacity-50"
            />
          </div>
          <Button data-testid="li-connect-create-btn" onClick={create} disabled={creating}>
            {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4 mr-2" /> Create campaign</>}
          </Button>
        </div>

        {/* Campaigns */}
        <div className="space-y-4">
          <div className="text-sm font-semibold text-muted-foreground">Your campaigns</div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : campaigns.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="li-connect-empty">
              No campaigns yet. Create one above.
            </div>
          ) : campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} onChanged={load} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

function CampaignCard({ campaign, onChanged }) {
  const [busy, setBusy] = useState("");
  const [showPeople, setShowPeople] = useState(false);
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const s = campaign.stats || {};

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); await onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    setBusy("");
  };
  const discover = () => act("discover", async () => {
    const { data } = await api.post(`/linkedin/connect/campaigns/${campaign.id}/discover`);
    const jobId = data.job_id;
    toast.info("Discovering engagers…", { description: "Scanning each page's recent posts for people who reacted or commented. This can take a few minutes for many pages — you can keep working." });
    // Poll up to ~15 min. The job keeps running server-side even if you navigate away.
    for (let i = 0; i < 180; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      let s;
      try { ({ data: s } = await api.get(`/linkedin/connect/campaigns/${campaign.id}/discover/${jobId}`)); }
      catch { continue; }
      if (s.status === "done") {
        const errs = s.errors || [];
        const scanned = s.posts_scanned || 0;
        if ((s.added || 0) > 0) {
          toast.success(`Found ${s.added} engaged ${s.added === 1 ? "person" : "people"}`, {
            description: `Scanned ${scanned} recent post${scanned === 1 ? "" : "s"}${errs.length ? ` · ${errs.length} page(s) skipped` : ""}`,
          });
        } else {
          toast.warning("Found 0 new people", {
            description: errs.length ? errs.slice(0, 3).join(" · ") : `Scanned ${scanned} posts — no new reactors/commenters (they may already be queued).`,
          });
        }
        break;
      }
      if (s.status === "error") { toast.error("Discovery failed", { description: s.error || "Please try again." }); break; }
    }
  });
  const runNow = () => act("run", async () => {
    const { data } = await api.post(`/linkedin/connect/campaigns/${campaign.id}/run-now`);
    toast.success(data.sent ? `Sent ${data.sent} invites` : (data.reason === "daily_limit_reached" ? "Daily limit already reached" : "Nothing to send"));
  });
  const toggle = () => act("toggle", async () => {
    await api.post(`/linkedin/connect/campaigns/${campaign.id}/status`, { status: campaign.status === "active" ? "paused" : "active" });
  });
  const sync = () => act("sync", async () => {
    const { data } = await api.post(`/linkedin/connect/campaigns/${campaign.id}/sync`);
    toast.success(`${data.accepted || 0} newly accepted · ${data.followups_sent || 0} follow-ups sent`,
      { description: data.error ? String(data.error) : "" });
  });
  const loadPeople = async () => {
    setShowPeople((v) => !v);
    if (!showPeople) {
      try { const { data } = await api.get(`/linkedin/connect/campaigns/${campaign.id}/candidates`); setPeople(data.candidates || []); }
      catch { /* ignore */ }
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${campaign.name}"? This removes the campaign and all ${s.candidates || 0} people in it. This can't be undone.`)) return;
    setBusy("delete");
    try {
      await api.delete(`/linkedin/connect/campaigns/${campaign.id}`);
      toast.success("Campaign deleted");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setBusy("");
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid={`li-connect-campaign-${campaign.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-semibold">{campaign.name}</div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] border font-semibold uppercase ${campaign.status === "active" ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/40" : "bg-muted text-muted-foreground border-border"}`} data-testid={`li-connect-status-${campaign.id}`}>{campaign.status}</span>
        <span className="text-xs text-muted-foreground">{campaign.daily_limit}/day · {(campaign.companies || []).length} companies</span>
        <span className="ml-auto text-xs text-muted-foreground">Today: {s.sent_today || 0}/{campaign.daily_limit}</span>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {STAT_META.map(({ key, label, icon: Icon, cls }) => (
          <div key={key} className="bg-muted/40 rounded-lg p-2.5 text-center">
            <Icon className={`w-4 h-4 mx-auto mb-1 ${cls}`} />
            <div className={`text-lg font-bold ${cls}`} data-testid={`li-connect-stat-${key}-${campaign.id}`}>{s[key] ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border">
        <Button size="sm" variant="secondary" onClick={discover} disabled={!!busy} data-testid={`li-connect-discover-${campaign.id}`}>
          {busy === "discover" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />} Discover people
        </Button>
        <Button size="sm" onClick={runNow} disabled={!!busy || campaign.status !== "active"} data-testid={`li-connect-run-${campaign.id}`}>
          {busy === "run" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send today's batch
        </Button>
        <Button size="sm" variant="outline" onClick={toggle} disabled={!!busy} data-testid={`li-connect-toggle-${campaign.id}`}>
          {campaign.status === "active" ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>}
        </Button>
        <Button size="sm" variant="secondary" onClick={sync} disabled={!!busy} data-testid={`li-connect-sync-${campaign.id}`} title="Check who accepted and send any follow-ups now due">
          {busy === "sync" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Sync accepts
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={!!busy} data-testid={`li-connect-edit-${campaign.id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" onClick={() => setMoving(true)} disabled={!!busy} data-testid={`li-connect-move-${campaign.id}`}>
          <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Move
        </Button>
        <Button size="sm" variant="outline" onClick={remove} disabled={!!busy} data-testid={`li-connect-delete-${campaign.id}`}
          className="text-red-400 hover:text-red-300 border-red-500/30 hover:bg-red-500/10">
          {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Delete
        </Button>
        <Button size="sm" variant="ghost" onClick={loadPeople} data-testid={`li-connect-people-${campaign.id}`} className="ml-auto">{showPeople ? "Hide" : "View"} people</Button>
      </div>

      <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
        <span>{(campaign.notes || []).length || (campaign.note ? 1 : 0)} note variations</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          Follow-up {campaign.followup_enabled ? "on (1–2 days after accept)" : "off"}
        </span>
      </div>

      {showPeople && (
        <div className="space-y-1.5 max-h-72 overflow-auto" data-testid={`li-connect-people-list-${campaign.id}`}>
          {people.length === 0 ? <div className="text-xs text-muted-foreground py-2">No people yet — run Discover.</div> :
            people.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name} {p.profile_url && <a href={p.profile_url} target="_blank" rel="noreferrer" className="text-signal-500"><ExternalLink className="w-3 h-3 inline" /></a>}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{p.headline || p.company}</div>
                </div>
                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${p.status === "sent" ? "text-signal-500 border-signal-500/40" : p.status === "accepted" ? "text-emerald-400 border-emerald-400/40" : p.status === "followed_up" ? "text-violet-400 border-violet-400/40" : p.status === "failed" ? "text-red-400 border-red-500/40" : "text-muted-foreground border-border"}`}>{p.status === "followed_up" ? "followed up" : p.status}</span>
              </div>
            ))}
        </div>
      )}

      {editing && (
        <EditCampaignModal
          campaign={campaign}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await onChanged(); }}
        />
      )}

      {moving && (
        <MoveCampaignModal
          campaign={campaign}
          onClose={() => setMoving(false)}
          onMoved={async () => { setMoving(false); await onChanged(); }}
        />
      )}
    </div>
  );
}

function MoveCampaignModal({ campaign, onClose, onMoved }) {
  const [targets, setTargets] = useState(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const s = campaign.stats || {};

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/linkedin/connect/movable-workspaces"); setTargets(data.workspaces || []); }
      catch { setTargets([]); }
    })();
  }, []);

  const move = async () => {
    if (!selected) { toast.error("Pick a destination workspace"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/linkedin/connect/campaigns/${campaign.id}/move`, { target_workspace_id: selected });
      const dest = (targets.find((t) => t.id === selected) || {}).name || "the workspace";
      toast.success(`Moved to ${dest}`, { description: `${data.moved_people || 0} discovered people came along. Switch to ${dest} to see it.` });
      await onMoved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not move campaign"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-16"
      onClick={() => !busy && onClose()} data-testid="li-connect-move-modal">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="text-sm font-semibold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-signal-500" /> Move campaign</div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="li-connect-move-close"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground">
            Move <b className="text-foreground">{campaign.name}</b> — with its {s.candidates || 0} discovered people and all stats — to another workspace you own. It will run from that workspace's connected LinkedIn account.
          </p>
          {targets === null ? (
            <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-muted-foreground" /></div>
          ) : targets.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">No other workspaces you own to move this into.</div>
          ) : (
            <div className="space-y-2">
              {targets.map((t) => (
                <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`li-connect-move-target-${t.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors flex items-center gap-2 ${selected === t.id ? "border-signal-500 bg-signal-500/10" : "border-border hover:bg-muted/40"}`}>
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-sm">{t.name}</span>
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${t.has_linkedin ? "text-emerald-400 border-emerald-400/40" : "text-amber-accent border-amber-accent/40"}`}>
                    {t.has_linkedin ? "LinkedIn connected" : "No LinkedIn"}
                  </span>
                </button>
              ))}
              {selected && !(targets.find((t) => t.id === selected) || {}).has_linkedin && (
                <p className="text-[11px] text-amber-accent">Heads up: this workspace has no LinkedIn account connected yet, so discovery/sending won't run until you connect one there.</p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="li-connect-move-cancel">Cancel</Button>
          <Button onClick={move} disabled={busy || !selected} data-testid="li-connect-move-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Moving…</> : "Move campaign"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditCampaignModal({ campaign, onClose, onSaved }) {
  const [name, setName] = useState(campaign.name || "");
  const [dailyLimit, setDailyLimit] = useState(campaign.daily_limit || 20);
  const [companies, setCompanies] = useState((campaign.companies || []).join("\n"));
  const [notes, setNotes] = useState(() => {
    const base = (campaign.notes && campaign.notes.length) ? campaign.notes : (campaign.note ? [campaign.note] : [""]);
    return [...base, "", "", "", "", ""].slice(0, 5);
  });
  const [followup, setFollowup] = useState(campaign.followup_message || "");
  const [followupEnabled, setFollowupEnabled] = useState(!!campaign.followup_enabled);
  const [saving, setSaving] = useState(false);

  const setNoteAt = (i, val) => setNotes((prev) => prev.map((n, idx) => (idx === i ? val : n)));

  const save = async () => {
    const list = companies.split("\n").map((c) => c.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one target company page"); return; }
    const cleanNotes = notes.map((n) => n.trim()).filter(Boolean);
    if (!cleanNotes.length) { toast.error("Add at least one connection note"); return; }
    setSaving(true);
    try {
      await api.patch(`/linkedin/connect/campaigns/${campaign.id}`, {
        name, companies: list, daily_limit: Number(dailyLimit),
        notes: cleanNotes, followup_message: followup.trim(),
        followup_enabled: followupEnabled && !!followup.trim(),
      });
      toast.success("Campaign updated");
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save changes"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-10 overflow-y-auto"
      onClick={() => !saving && onClose()} data-testid="li-connect-edit-modal">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="text-sm font-semibold flex items-center gap-2"><Pencil className="w-4 h-4 text-signal-500" /> Edit campaign</div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="li-connect-edit-close"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Campaign name</label>
              <Input data-testid="li-connect-edit-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Connection requests per day</label>
              <div className="flex gap-2 mt-1">
                {[15, 20, 25].map((n) => (
                  <button key={n} data-testid={`li-connect-edit-limit-${n}`} onClick={() => setDailyLimit(n)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${Number(dailyLimit) === n ? "bg-signal-500 text-white border-signal-500" : "border-border text-muted-foreground"}`}>{n}/day</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Target company pages (one per line)</label>
            <Textarea data-testid="li-connect-edit-companies" value={companies} onChange={(e) => setCompanies(e.target.value)} rows={4} className="mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Connection notes — up to 5 (max 300 chars each)</label>
            <div className="space-y-2 mt-1.5">
              {notes.map((n, i) => (
                <div key={i} className="relative">
                  <span className="absolute left-2 top-2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-signal-500/15 text-signal-500 z-10">#{i + 1}</span>
                  <Textarea data-testid={`li-connect-edit-note-${i}`} value={n} onChange={(e) => setNoteAt(i, e.target.value)} rows={2} maxLength={300} className="text-sm pl-10" placeholder={`Connection note variation ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-violet-400/30 bg-violet-400/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-semibold">After they accept — follow-up DM</span>
              <button type="button" onClick={() => setFollowupEnabled((v) => !v)} data-testid="li-connect-edit-followup-toggle"
                className={`ml-auto relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${followupEnabled ? "bg-violet-500" : "bg-muted"}`}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${followupEnabled ? "translate-x-5" : "translate-x-1"}`} />
              </button>
            </div>
            <Textarea data-testid="li-connect-edit-followup-msg" value={followup} onChange={(e) => setFollowup(e.target.value)} rows={3} maxLength={900} disabled={!followupEnabled} className="text-sm disabled:opacity-50" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="li-connect-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="li-connect-edit-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
